import { CIMSSingleColumn } from 'components/CIMSTable/CIMSColumn/types'
import { HelpMessage } from 'utils/constants'
import { Submittal } from '../../../../../../SubmittalTrackerContext/types'
import { CASStatus } from 'shared'
import { useCallback } from 'react'
import CIMSChecklist from 'components/CIMSChecklist'
import { CIMSTags } from 'components/CIMSTags'
import { useAuthContext } from 'context/AuthContext'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useSetCasToCasRoutingGroupMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useSubmittalTrackerContext } from '../../../../../../SubmittalTrackerContext'
import { errorHandler } from '../../../../../../../../../../../../../hooks/errorHandler'

export function useRoutingGroups(): CIMSSingleColumn<Submittal> {
  const { user } = useAuthContext()
  const {
    permissions: { canReviewAll },
    refetch,
  } = useSubmittalTrackerContext()
  const canAccept = useCallback(
    (
      routingGroupId: string,
      c2crg: Submittal['casToCASRoutingGroups']
    ): boolean => {
      if (!user?.id) {
        errorHandler('UserId must be defined')
        return false
      }
      if (canReviewAll) return true
      const inThisGroup = c2crg
        .filter(({ routingGroup: { users } }) =>
          users?.map(({ id }) => id).includes(user.id)
        )
        .map(({ routingGroup: { id } }) => id)
        .includes(routingGroupId)
      return inThisGroup
    },
    [canReviewAll, user]
  )
  const [set] = useSetCasToCasRoutingGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      setCASToCASRoutingGroup: {
        accepted,
        routingGroup: { name },
      },
    }) =>
      simpleSuccessNotification(
        `${name} successfully ${accepted ? 'accepted' : 'reset'}`
      ),
  })
  return {
    title: 'Routing Groups',
    help: HelpMessage.CAS.SubmittalTracker.RoutingGroups,
    render: ({ casToCASRoutingGroups, statusId, deleted, id: submittalId }) => {
      return statusId === CASStatus.GroupReview.id ? (
        <CIMSChecklist
          items={casToCASRoutingGroups.map(
            ({
              routingGroup: { name, id: routingGroupId, description },
              accepted,
            }) => ({
              key: `${routingGroupId}-${submittalId}`,
              checked: accepted,
              tag: { children: name, tooltip: description },
              disabled: [
                {
                  disabled: !canAccept(routingGroupId, casToCASRoutingGroups),
                  reason: 'missing permission',
                },
                {
                  disabled: deleted,
                  reason: { why: 'inactive', entityName: 'Submittal' },
                },
              ],
              onChange: async () => {
                await set({
                  variables: {
                    input: { submittalId, routingGroupId, accepted: !accepted },
                  },
                })
                refetch()
              },
            })
          )}
        />
      ) : (
        <CIMSTags
          tags={casToCASRoutingGroups?.map(
            ({ routingGroup: { name, description } }) => ({
              text: name,
              tooltip: description,
              style: { verticalAlign: 'top' },
            })
          )}
        />
      )
    },
    width: 200,
    filter: {
      type: 'checkboxes',
      items: (records) =>
        records
          ?.flatMap(({ casToCASRoutingGroups }) => casToCASRoutingGroups)
          .map(({ routingGroup: { id, name, description } }) => ({
            text: name,
            value: id,
            tooltip: description,
          })),
      value: (routingGroupId, { casToCASRoutingGroups, statusId }) => {
        const accepted = !!casToCASRoutingGroups.find(
          ({ routingGroup }) => routingGroup?.id === routingGroupId
        )?.accepted
        // if closed/unlocked and not accepted, it's implied to be accepted
        if (
          !accepted &&
          (statusId === CASStatus.Closed.id ||
            statusId === CASStatus.Unlocked.id)
        )
          return true
        if (
          statusId === CASStatus.RevisionsRequested.id ||
          statusId === CASStatus.RouterReview.id
        )
          return false
        return accepted
      },
      skip: (routingGroupId, { casToCASRoutingGroups }) => {
        // don't care if not in c2rg
        const notInGroups = casToCASRoutingGroups.every(
          (c2crg) => c2crg.routingGroup.id !== routingGroupId
        )
        return notInGroups || !casToCASRoutingGroups.length
      },
      labels: { true: 'Accepted', false: 'Awaiting Acceptance' },
    },
  }
}
